// Site-wide copy + metadata for PropertyActions. Library/discovery-led framing
// (RamsDocs layout) over the original PropertyActions positioning: turn statutory
// compliance reports into reviewed action trackers and audit-ready evidence packs.

export const SITE = {
  name: "PropertyActions",
  url: "https://propertyactions.co.uk",
  domain: "propertyactions.co.uk",
  source: "propertyactions.co.uk", // signups DB partition key
  email: "hello@propertyactions.co.uk",
  description:
    "Turn fire, asbestos, electrical, gas, water and lift compliance reports into reviewed action trackers, evidence chases and audit-ready packs. Built for UK managing agents, block managers and FM teams.",
};

export const HERO = {
  eyebrow: "UK property compliance admin",
  title: "Find the compliance module. Turn the report into actions.",
  titleAccent: "Turn the report into actions.",
  sub: "Browse the compliance library, then upload a Fire Risk Assessment, EICR, asbestos survey or legionella risk assessment. PropertyActions extracts the actions, owners, due dates and evidence requirements — and keeps everything audit-ready.",
  proofPills: [
    "Candidate actions need review",
    "Assessor still signs off",
    "UK / EU-hosted data",
    "Free for your first property",
  ],
};

// Standards strip under the hero — real regulations the library is built around.
export const STANDARDS = [
  "Fire Safety Order 2005",
  "CAR 2012",
  "ESS (PRS) 2020",
  "GSIUR 1998",
  "L8 / HSG274",
  "LOLER 1998",
];

export const POSITIONING =
  "The report is not the hard part. The follow-through is.";

export const HOW_IT_WORKS = [
  {
    step: 1,
    title: "Pick the compliance module",
    body: "Start with fire, then reuse the same workflow for asbestos, EICR, water, gas and lift. Each module knows the duties, intervals and evidence its reports require.",
  },
  {
    step: 2,
    title: "Upload the report",
    body: "Drop in the FRA, EICR, survey or risk assessment PDF. The source report stays attached to the property record so every action traces back to its finding.",
  },
  {
    step: 3,
    title: "Review the extracted actions",
    body: "Approve, edit or reject AI-extracted candidate actions before anything goes live. Set owners, due dates and the evidence each action needs to close.",
  },
  {
    step: 4,
    title: "Chase, evidence, export",
    body: "Send contractors a focused closeout link, collect proof against each action, and export an audit-ready pack with the open gaps clearly labelled.",
  },
];

// "The old way" vs PropertyActions — the RamsDocs comparison table, mapped to
// the compliance-admin pain.
export const COMPARISON: { old: string; prop: string }[] = [
  { old: "Retype every action from the PDF into a spreadsheet", prop: "Actions extracted from the report, traced to the source page" },
  { old: "Owners and due dates live in email threads", prop: "Each action carries an owner, due date and evidence request" },
  { old: "Deadlines expire quietly until the next audit panic", prop: "Reminders chase what's due before it slips" },
  { old: "Evidence scattered across inboxes and job sheets", prop: "Photos, certificates and sign-off attached to each action" },
  { old: "Rebuild the audit pack by hand every time", prop: "Export a closeout pack in one click, gaps clearly shown" },
];

// Pre-submission / evidence readiness examples for the checker section.
export const EVIDENCE_EXAMPLES = [
  "Immediate (high-priority) actions still open past their due date",
  "Closed actions with no uploaded proof attached",
  "AI-extracted candidates never reviewed by a competent person",
  "Reports approaching their statutory review or re-inspection interval",
  "Contractor evidence requested but not yet returned",
];

// What PropertyActions is deliberately NOT — guardrails surface this everywhere.
export const GUARDRAIL =
  "PropertyActions is an admin and recordkeeping tool. It does not carry out statutory assessments, replace competent professionals, or guarantee acceptance by a regulator, insurer, client or enforcing authority. The responsible person or dutyholder retains full legal accountability.";
